import {
  isBulkPricingPercent,
  isBulkPricingPrice,
  type Product,
} from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import type {
  CloudinaryAsset,
  NewsItem,
  SanityImage,
} from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import { removeUndefined } from '@bulknutrients/bulk-shared-lib/utils/helpers';
import { getImageSrcUrl } from '@components/Media/Image/Image';

export const convertFAQToStructedData = (
  faqs: { question: string; answer: string }[]
) => {
  if (faqs.length === 0) return null;

  return {
    '@context': 'http://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

export const convertNewsArticleToStructuredData = (
  newsItem: NewsItem,
  baseUrl: string
) => ({
  '@context': 'http://schema.org',
  '@type': 'NewsArticle',
  headline: newsItem.title,
  datePublished: newsItem.datetime,
  author: {
    '@type': 'Organization',
    brand: 'Bulk Nutrients',
    url: `${baseUrl}/experts/bulk-nutrients`,
    // url: `${process.env.NEXT_PUBLIC_SERVER_URL || Constants.expoConfig?.extra?.SERVER_URL}/experts/bulk-nutrients`,
  },
});

export const getStructuredDataImageUrl = (
  image: SanityImage | CloudinaryAsset
) => {
  try {
    return getImageSrcUrl({ image: image, width: 500 });
  } catch (error) {
    console.error(error);
  }
};

const calculateBulkPrice = (basePrice: number, tier: any) => {
  if (isBulkPricingPrice(tier)) {
    // Fixed price
    return tier.price;
  } else if (isBulkPricingPercent(tier)) {
    // Percentage discount
    return basePrice * (1 - tier.percentOff / 100);
  } else {
    // Fixed amount discount
    return basePrice - tier.priceAdjustment;
  }
};

const createBulkPriceSpecifications = (product: Product) => {
  const specifications: Array<{
    '@type': string;
    priceCurrency: string;
    price: number;
    eligibleQuantity: {
      '@type': string;
      value?: number;
      minValue?: number;
      maxValue?: number;
      unitCode: string;
    };
  }> = [];

  const basePrice = product.pricing.price.value;

  // Add base price (quantity 1)
  specifications.push({
    '@type': 'UnitPriceSpecification',
    priceCurrency: 'AUD',
    price: basePrice,
    eligibleQuantity: {
      '@type': 'QuantitativeValue',
      value: 1,
      unitCode: 'C62', // pieces/units
    },
  });

  return specifications;
};

const getProductWeight = (product: Product) => {
  const packageSize = product.options.filter(
    (o) => o.displayName.toLowerCase() === 'package size'
  )[0];
  const label = packageSize?.values.filter((v) => v.isDefault)[0]?.label;

  // Split out package size to volume and unit
  const parts = label?.match(/^[\d.]+|[a-zA-Z]+/g);
  if (!parts || parts.length < 2) return undefined;

  const weightValue = Number(parts?.[0] as string);
  const unit = (parts?.[1] as string).toLowerCase();

  if (!weightValue || !unit) return undefined;

  // Convert to grams and use proper unit code
  let unitCode = 'GRM'; // Default to grams

  if (unit === 'kg' || unit === 'kilogram') {
    unitCode = 'KGM';
  }

  return {
    '@type': 'QuantitativeValue',
    value: weightValue,
    unitCode: unitCode,
    unitText: unitCode === 'KGM' ? 'kilograms' : 'grams',
  };
};

const getProductSize = (product: Product) => {
  const sizeOption = product.options.filter(
    (o) => o.displayName.toLowerCase() === 'size'
  )[0];
  if (!sizeOption) return undefined;

  // For the default/selected size
  const defaultSize =
    sizeOption.values.filter((v) => v.isDefault)[0]?.label ??
    sizeOption.values[0]?.label;

  return {
    '@type': 'SizeSpecification',
    name: defaultSize, // e.g., "Large"
    suggestedMeasurement: sizeOption.values.map((v) => v.label), // include all available sizes
  };
};

const getProductQuantity = (product: Product) => {
  const packageSize = product.options.filter(
    (o) => o.displayName === 'Package Size'
  )[0];
  const label = packageSize?.values.filter((v) => v.isDefault)[0]?.label;

  // Look for capsule/tablet count patterns like "120 capsules", "60 tabs"
  const capsuleMatch = label?.match(
    /(\d+)\s*(capsules?|caps?|tablets?|tabs?)/i
  );
  if (capsuleMatch) {
    return {
      '@type': 'QuantitativeValue',
      value: Number(capsuleMatch[1]),
      unitCode: 'C62', // pieces/units
      unitText: capsuleMatch[2].toLowerCase(),
    };
  }

  return undefined;
};

const getProductDimensions = (product: Product) => {
  // Check if this is a clothing item
  const hasSize = product.options.some(
    (o) => o.displayName.toLowerCase() === 'size'
  );
  if (hasSize) {
    return getProductSize(product);
  }

  // Check if this is a capsule/tablet product
  const packageSize = product.options.filter(
    (o) => o.displayName.toLowerCase() === 'package size'
  )[0];
  const label = packageSize?.values.filter((v) => v.isDefault)[0]?.label;

  if (label?.match(/capsules?|caps?|tablets?|tabs?/i)) {
    return getProductQuantity(product);
  }

  // Default to weight for powder/liquid products
  return getProductWeight(product);
};

const extractNutritionPanels = (product: Product) => {
  if (!product.description) return [];

  // Find the description object with "Nutrition Panels" content
  const nutritionDescription = product.description.find(
    (desc) => desc.contentDescription === 'Nutrition Panels'
  );

  if (!nutritionDescription?.contentBody) return [];

  // Extract all nutrition panels from the content body
  return nutritionDescription.contentBody.filter(
    (content) => content._type === 'nutritionPanel'
  );
};

const parseNutritionValues = (rows: any[]) => {
  const nutritionData: any = {};

  // Skip the header row (first row)
  const dataRows = rows.slice(1);

  dataRows.forEach((row) => {
    if (row.cells && row.cells.length >= 3) {
      const nutrientName = row.cells[0].toLowerCase();
      const perServingValue = row.cells[1];
      const per100gValue = row.cells[2];

      // Map common nutrient names to schema.org properties
      if (nutrientName.includes('energy')) {
        nutritionData.calories = perServingValue;
      } else if (nutrientName.includes('protein')) {
        nutritionData.proteinContent = `${perServingValue}g`;
      } else if (
        nutrientName.includes('fats') &&
        !nutrientName.includes('saturated')
      ) {
        nutritionData.fatContent = `${perServingValue}g`;
      } else if (nutrientName.includes('saturated')) {
        nutritionData.saturatedFatContent = `${perServingValue}g`;
      } else if (
        nutrientName.includes('carbohydrates') &&
        !nutrientName.includes('sugars')
      ) {
        nutritionData.carbohydrateContent = `${perServingValue}g`;
      } else if (nutrientName.includes('sugars')) {
        nutritionData.sugarContent = `${perServingValue}g`;
      } else if (nutrientName.includes('sodium')) {
        nutritionData.sodiumContent = `${perServingValue}mg`;
      }
    }
  });

  return nutritionData;
};

const extractNutritionData = (nutritionPanel: any) => {
  if (!nutritionPanel?.content?.rows) return undefined;

  const nutritionValues = parseNutritionValues(nutritionPanel.content.rows);

  return {
    '@type': 'NutritionInformation',
    servingSize:
      nutritionPanel.subTitle2.slice(
        nutritionPanel.subTitle2.indexOf(' ') + 1
      ) || undefined,
    ...nutritionValues,
  };
};

interface VariantStructuredData {
  '@type': string | string[];
  name: string;
  sku: string;
  image: (string | undefined)[];
  additionalProperty?: Array<{
    '@type': string;
    name: string;
    value: string;
  }>;
  nutrition?: any;
  offers: {
    '@type': string;
    price: number;
    priceCurrency: string;
    availability: string;
  };
}

const createVariantStructuredData = (product: Product) => {
  if (!product.variants || product.variants.length === 0) return undefined;

  const nutritionPanels = extractNutritionPanels(product);
  const variants: Array<VariantStructuredData> = [];

  // Generate structured data for each actual variant
  product.variants.forEach((variant) => {
    // Try to find matching nutrition panel by variant option values
    const variantLabels = variant.options.map((option) =>
      option.label.toLowerCase()
    );
    const matchingNutritionPanel = nutritionPanels.find(
      (panel) =>
        panel.title &&
        variantLabels.some(
          (label) =>
            panel.title.toLowerCase().includes(label) ||
            label.includes(panel.title.toLowerCase().split(' ')[0])
        )
    );

    const variantName =
      variant.options.length > 0
        ? `${product.name} - ${variant.options.map((opt) => opt.label).join(', ')}`
        : product.name;

    const variantData: VariantStructuredData = {
      '@type': ['Product', 'MenuItem'],
      name: variantName,
      image: product.images.map((image) => getStructuredDataImageUrl(image)),
      sku: variant.sku,
      additionalProperty: variant.options.map((option) => ({
        '@type': 'PropertyValue',
        name: option.displayName,
        value: option.label,
      })),
      nutrition: matchingNutritionPanel
        ? extractNutritionData(matchingNutritionPanel)
        : undefined,
      offers: {
        '@type': 'Offer',
        price: variant.pricing.price.value,
        priceCurrency: 'AUD',
        availability: 'https://schema.org/InStock', // Variants are generally in stock if they exist
      },
    };

    // Remove undefined nutrition data
    if (
      !variantData.nutrition ||
      Object.keys(variantData.nutrition).length <= 1
    ) {
      delete variantData.nutrition;
    }

    // If there are no options, remove additionalProperty
    if (variantData.additionalProperty?.length === 0) {
      delete variantData.additionalProperty;
    }

    variants.push(variantData);
  });

  return variants.length > 0 ? variants : undefined;
};

export const createProductStructuredData = (
  product: Product,
  baseUrl: string
) => {
  const threeMonthsDate = new Date();
  threeMonthsDate.setMonth(threeMonthsDate.getMonth() + 3);

  try {
    const dimensions = getProductDimensions(product);
    const variants = createVariantStructuredData(product);

    return removeUndefined({
      '@context': 'https://schema.org',
      '@type': 'ProductGroup',
      name: product.name,
      image: product.images.map((image) => getStructuredDataImageUrl(image)),
      description: product.shortDescription,
      offers: {
        '@type': 'Offer',
        availability: product.serverSideStock
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        url: `${baseUrl}${product.slug}`,
        // url: `${process.env.NEXT_PUBLIC_SERVER_URL}${product.slug}`,
        priceSpecification: createBulkPriceSpecifications(product),
        priceValidUntil: `${threeMonthsDate.getFullYear()}-${`0${
          threeMonthsDate.getMonth() + 1
        }`.slice(-2)}-${(`0${threeMonthsDate.getDate()}`).slice(-2)}`,
      },
      hasVariant: variants,
      size: { ...dimensions },
      aggregateRating:
        product.totalReviews > 0
          ? {
              '@type': 'AggregateRating',
              ratingValue: product.averageRating,
              reviewCount: product.totalReviews,
            }
          : undefined,
    });
  } catch (error) {
    console.error('Error creating structured data for product:', error);
    return null;
  }
};
